import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Grid,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
  Popover,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DateTime } from 'luxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Axios from 'axios';
import getCookie from '../../utils/getCookies';
import { tokens } from '../../theme';
import { formatDefaultTimes } from '../../utils/makeupScheduleDefaults';

export default function MakeupStartTimesModal({ open, onClose }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [startTimesData, setStartTimesData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  
  // Form states
  const [selectedStartTimes, setSelectedStartTimes] = useState([]);
  const [saturdayStartTimes, setSaturdayStartTimes] = useState([]);
  const [sundayStartTimes, setSundayStartTimes] = useState([]);
  const [validFrom, setValidFrom] = useState(null);
  const [validTo, setValidTo] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);

  // Generate all possible time slots from 13:00 to 22:30
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 13; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 22 && minute > 30) break;
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeStr);
      }
    }
    return slots;
  };

  const allTimeSlots = generateTimeSlots();

  // Fetch existing start times data
  const fetchStartTimes = async () => {
    setLoading(true);
    try {
      const token = getCookie('_auth');
      const response = await Axios.get(
        `${process.env.REACT_APP_URL}/api/v1/makeup-start-times`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data) {
        setStartTimesData(response.data);
      }
    } catch (err) {
      console.error('Error fetching start times:', err);
      if (err.response?.status === 404) {
        setStartTimesData([]);
      } else {
        setError('Failed to fetch start times data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchStartTimes();
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setSelectedStartTimes([]);
    setSaturdayStartTimes([]);
    setSundayStartTimes([]);
    setValidFrom(null);
    setValidTo(null);
    setEditMode(false);
    setCurrentEditId(null);
    setError('');
    setSuccessMessage('');
  };

  const handleEdit = (item) => {
    setEditMode(true);
    setCurrentEditId(item.id);
    setSelectedStartTimes(item.valid_start_times || []);
    setSaturdayStartTimes(item.saturday_start_times || []);
    setSundayStartTimes(item.sunday_start_times || []);
    setValidFrom(dayjs(item.valid_from));
    setValidTo(dayjs(item.valid_to));
    setError('');
    setSuccessMessage('');
  };

  const handleDeleteClick = (event, id) => {
    setDeleteAnchorEl(event.currentTarget);
    setDeleteItemId(id);
  };

  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
    setDeleteItemId(null);
  };

  const handleDeleteConfirm = async () => {
    // Close popover immediately to prevent positioning issues
    handleDeleteClose();
    
    setLoading(true);
    try {
      const token = getCookie('_auth');
      await Axios.delete(
        `${process.env.REACT_APP_URL}/api/v1/makeup-start-times/${deleteItemId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccessMessage('Start times configuration deleted successfully');
      await fetchStartTimes();
    } catch (err) {
      console.error('Error deleting start times:', err);
      setError('Failed to delete start times configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (selectedStartTimes.length === 0) {
      setError('Please select at least one start time');
      return;
    }
    if (!validFrom || !validTo) {
      setError('Please select both valid from and valid to dates');
      return;
    }
    if (validFrom.isAfter(validTo)) {
      setError('Valid to date must be after valid from date');
      return;
    }

    setLoading(true);
    setError('');
    
    const data = {
      valid_start_times: selectedStartTimes,
      saturday_start_times: saturdayStartTimes,
      sunday_start_times: sundayStartTimes,
      valid_from: validFrom.format('YYYY-MM-DD'),
      valid_to: validTo.format('YYYY-MM-DD'),
    };

    try {
      const token = getCookie('_auth');
      if (editMode && currentEditId) {
        // Update existing
        await Axios.put(
          `${process.env.REACT_APP_URL}/api/v1/makeup-start-times/${currentEditId}`,
          data,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSuccessMessage('Start times configuration updated successfully');
      } else {
        // Create new
        await Axios.post(
          `${process.env.REACT_APP_URL}/api/v1/makeup-start-times`,
          data,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSuccessMessage('Start times configuration created successfully');
      }
      
      await fetchStartTimes();
      resetForm();
    } catch (err) {
      console.error('Error saving start times:', err);
      setError(err.response?.data?.error || 'Failed to save start times configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTimeToggle = (event, newTimes) => {
    setSelectedStartTimes(newTimes || []);
  };

  const handleSaturdayTimeToggle = (event, newTimes) => {
    setSaturdayStartTimes(newTimes || []);
  };

  const handleSundayTimeToggle = (event, newTimes) => {
    setSundayStartTimes(newTimes || []);
  };

  const formatDate = (dateStr) => {
    return DateTime.fromISO(dateStr).toLocaleString(DateTime.DATE_MED);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: colors.primary[50] }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>
          Manage Makeup Start Times
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: colors.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3, position: 'relative', minHeight: '400px' }}>
        {loading && (
          <Box sx={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'white',
              p: 3,
              borderRadius: 2,
              boxShadow: 3
            }}>
              <CircularProgress size={48} />
              <Typography sx={{ mt: 2, color: colors.primary[400] }}>
                Loading...
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* Default Times Information */}
        {!editMode && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Default Start Times (Used when no configuration matches a date)
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Weekdays (Mon-Fri):</strong> {formatDefaultTimes('weekday')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Saturday:</strong> {formatDefaultTimes('saturday')}
              </Typography>
              <Typography variant="body2">
                <strong>Sunday:</strong> {formatDefaultTimes('sunday')} (when Sunday scheduling is enabled)
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Existing Configurations */}
        {!editMode && !loading && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: colors.primary[400] }}>
                Existing Configurations
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setEditMode(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                Add New Configuration
              </Button>
            </Box>

            {startTimesData.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: colors.grey[50] }}>
                <Typography variant="body1" sx={{ color: colors.grey[600] }}>
                  No start times configurations found. Click "Add New Configuration" to create one.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: colors.grey[100] }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Valid From</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Valid To</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Weekday Start Times</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Saturday Times</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sunday Times</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {startTimesData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatDate(item.valid_from)}</TableCell>
                        <TableCell>{formatDate(item.valid_to)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.valid_start_times.map((time) => (
                              <Chip
                                key={time}
                                label={time}
                                size="small"
                                sx={{ 
                                  bgcolor: '#e3f2fd',
                                  color: '#1565c0',
                                  fontWeight: 600,
                                  border: '1px solid #90caf9'
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {item.saturday_start_times && item.saturday_start_times.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {item.saturday_start_times.map((time) => (
                                <Chip
                                  key={time}
                                  label={time}
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#e8f5e9',
                                    color: '#2e7d32',
                                    fontWeight: 600,
                                    border: '1px solid #a5d6a7'
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.sunday_start_times && item.sunday_start_times.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {item.sunday_start_times.map((time) => (
                                <Chip
                                  key={time}
                                  label={time}
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#f3e5f5',
                                    color: '#7b1fa2',
                                    fontWeight: 600,
                                    border: '1px solid #ce93d8'
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleEdit(item)}
                            sx={{ color: colors.blueAccent[500] }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={(e) => handleDeleteClick(e, item.id)}
                            sx={{ color: colors.redAccent[500] }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Add/Edit Form */}
        {editMode && !loading && (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: colors.primary[400] }}>
              {currentEditId ? 'Edit Configuration' : 'New Configuration'}
            </Typography>

            {/* Date Range Selection */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Valid From"
                    value={validFrom}
                    onChange={setValidFrom}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Valid To"
                    value={validTo}
                    onChange={setValidTo}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Regular Start Times */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: colors.primary[400] }}>
                Weekday Start Times (Required)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: colors.grey[600] }}>
                Select the available start times for makeup classes on weekdays (Monday-Friday).
                <br />
                <strong>Default:</strong> {formatDefaultTimes('weekday')}
              </Typography>
              <ToggleButtonGroup
                value={selectedStartTimes}
                onChange={handleStartTimeToggle}
                aria-label="start times"
                multiple
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {allTimeSlots.map((time) => (
                  <ToggleButton
                    key={time}
                    value={time}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: colors.blueAccent[500],
                        color: 'white',
                        '&:hover': {
                          bgcolor: colors.blueAccent[600],
                        },
                      },
                    }}
                  >
                    {time}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Saturday Start Times */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: colors.primary[400] }}>
                Saturday Start Times (Optional)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: colors.grey[600] }}>
                Select the available start times for Saturdays only.
                <br />
                <strong>Default:</strong> {formatDefaultTimes('saturday')}
              </Typography>
              <ToggleButtonGroup
                value={saturdayStartTimes}
                onChange={handleSaturdayTimeToggle}
                aria-label="saturday times"
                multiple
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {allTimeSlots.map((time) => (
                  <ToggleButton
                    key={time}
                    value={time}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: '#2e7d32',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#1b5e20',
                        },
                      },
                    }}
                  >
                    {time}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Sunday Start Times */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: colors.primary[400] }}>
                Sunday Start Times (Optional)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: colors.grey[600] }}>
                Select the available start times for Sundays only.
                <br />
                <strong>Default:</strong> {formatDefaultTimes('sunday')} (when Sunday scheduling is enabled)
              </Typography>
              <ToggleButtonGroup
                value={sundayStartTimes}
                onChange={handleSundayTimeToggle}
                aria-label="sunday times"
                multiple
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {allTimeSlots.map((time) => (
                  <ToggleButton
                    key={time}
                    value={time}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: '#7b1fa2',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#6a1b9a',
                        },
                      },
                    }}
                  >
                    {time}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={resetForm}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{ textTransform: 'none', fontWeight: 'bold' }}
              >
                {currentEditId ? 'Update Configuration' : 'Create Configuration'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete this configuration?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={handleDeleteClose}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>
    </Dialog>
  );
}